import * as React from 'react';
import { createInstance } from '@module-federation/enhanced/runtime';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { Box } from '@locus-taxy/encore-mui';
import CustomAppEntityHeader from '../components/EntityDetails/CustomAppEntityHeader';
import { useClientId } from '../hooks/useClientId';
import { useAuthIntegration } from '../api/network/auth';
import type { EntityDetailsAction } from '../types/actions';
import * as MUI from '@locus-taxy/encore-mui';
import * as Network from '@locus-taxy/encore-ui-network';
import * as Toolkit from '@locus-taxy/encore-ui-toolkit';
import { CUSTOM_APP_BASE_URL } from '../config';

const mf = createInstance({
  name: 'host',
  remotes: [
    {
      name: 'tour-edit',
      entry: `${CUSTOM_APP_BASE_URL}/tour-edit/mf-manifest.json`,
    },
  ],
  shared: {
    react: {
      version: '19.1.1',
      scope: 'default',
      lib: () => React,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-dom': {
      version: '19.1.1',
      scope: 'default',
      lib: () => ReactDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-router-dom': {
      version: '7.6.3',
      scope: 'default',
      lib: () => ReactRouterDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^7.6.3',
      },
    },
    '@locus-taxy/encore-mui': {
      version: '1.0.0-beta.28',
      scope: 'default',
      lib: () => MUI,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.0.0-beta.28',
      },
    },
    '@locus-taxy/encore-ui-network': {
      version: '3.1.0',
      scope: 'default',
      lib: () => Network,
      shareConfig: {
        singleton: true,
        requiredVersion: '^3.1.0',
      },
    },
    '@locus-taxy/encore-ui-toolkit': {
      version: '5.0.0',
      scope: 'default',
      lib: () => Toolkit,
      shareConfig: {
        singleton: true,
        requiredVersion: '^5.0.0',
      },
    },
  },
});

interface TourEditsContainerProps {
  onClose?: () => void;
  selectedTours?: string[];
  entityName?: string;
  entityId?: string;
  entityNameToShow?: string;
  entityTitle?: string;
  entityData?: Record<string, unknown>;
  actions?: EntityDetailsAction[];
  onActionClick?: (action: EntityDetailsAction) => void;
  onViewSchema?: () => void;
}

export const TourEditsContainer = ({
  onClose: _onClose,
  selectedTours,
  entityName = 'tour',
  entityId = '',
  entityNameToShow = 'Edit Tours',
  entityTitle = 'Tour',
  entityData = {},
  actions = [],
  onActionClick = () => {},
  onViewSchema,
}: TourEditsContainerProps) => {
  const clientId = useClientId();
  const { token } = useAuthIntegration();
  type TourEditMfeProps = {
    selectedTours?: string[];
    clientId?: string;
    authToken?: string;
  };

  type TourEditRemoteModule = {
    default: React.ComponentType<TourEditMfeProps>;
  };

  const TourEditsMfe = React.lazy(() => {
    return mf.loadRemote('tour-edit/App').then((value: unknown) => {
      const { default: DefaultExport } = value as TourEditRemoteModule;
      const Component = DefaultExport;
      return { default: Component as React.ComponentType<TourEditMfeProps> };
    });
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <CustomAppEntityHeader
        entityName={entityName}
        entityId={entityId}
        entityNameToShow={entityNameToShow}
        entityTitle={entityTitle}
        entityData={entityData}
        actions={actions}
        onActionClick={onActionClick}
        onViewSchema={onViewSchema}
      />

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <React.Suspense fallback={<LoadingSpinner />}>
          <TourEditsMfe
            selectedTours={selectedTours}
            clientId={clientId}
            authToken={token ?? undefined}
          />
        </React.Suspense>
      </Box>
    </Box>
  );
};
